<template>
  <aside class="mc-sidebar-liveview" data-testid="technician-sidebar">

    <div class="mc-sidebar-liveview__wrapper">
      <b-tabs nav-class="mc-sidebar-liveview__nav" content-class="mc-sidebar-liveview__content">
        <b-tab active>
          <template #title>
            <i class="mc-fa fas fa-user-hard-hat me-1" aria-hidden="true"></i>
            {{ $t('location.sideBar.tab.technician') }}
          </template>

          <div class="mc-sidebar-liveview__fleet">
            <div class="mc-sidebar-liveview__fleet-inner">
              <!-- ===== Header: Filter + Search (sticky) ===== -->
              <header class="mc-sidebar-liveview__sticky-header">
                <!-- Filter status -->
                <div class="mc-filter-group" role="group" aria-label="Filter status teknisi">
                  <button type="button" class="mc-filter-chip" :class="{ 'is-active is-all': activeButtons.all }"
                    data-testid="filter-all" @click="changeActiveButton('all')">
                    <span class="mc-filter-chip__label">{{ $t('location.sideBar.status.all') }}</span>
                    <span class="mc-filter-chip__count">{{ counts.all }}</span>
                  </button>
                  <button type="button" class="mc-filter-chip" :class="{ 'is-active is-busy': activeButtons.busy }"
                    data-testid="filter-busy" @click="changeActiveButton('busy')">
                    <span class="status-dot status-dot--busy" aria-hidden="true"></span>
                    <span class="mc-filter-chip__label">{{ $t('location.sideBar.status.busy') }}</span>
                    <span class="mc-filter-chip__count">{{ counts.busy }}</span>
                  </button>
                  <button type="button" class="mc-filter-chip"
                    :class="{ 'is-active is-available': activeButtons.available }" data-testid="filter-available"
                    @click="changeActiveButton('available')">
                    <span class="status-dot status-dot--available" aria-hidden="true"></span>
                    <span class="mc-filter-chip__label">{{ $t('location.sideBar.status.available') }}</span>
                    <span class="mc-filter-chip__count">{{ counts.available }}</span>
                  </button>
                  <button type="button" class="mc-filter-chip"
                    :class="{ 'is-active is-niy': activeButtons.notInstalledYet }" data-testid="filter-niy"
                    @click="changeActiveButton('notInstalledYet')">
                    <span class="status-dot status-dot--niy" aria-hidden="true"></span>
                    <span class="mc-filter-chip__label">{{ $t('location.sideBar.status.notInstalledYet') }}</span>
                    <span class="mc-filter-chip__count">{{ counts.niy }}</span>
                  </button>
                </div>

                <!-- Search -->
                <b-input-group class="mc-sidebar-liveview__search mt-3">
                  <b-input-group-text>
                    <i class="icon-outline_search" aria-hidden="true" />
                  </b-input-group-text>
                  <b-form-input v-model="search.name" type="search" data-testid="sidebar-search-input"
                    :placeholder="`${$t('global.search')} teknisi...`" aria-label="Cari teknisi" />
                </b-input-group>

                <!-- Ringkasan hasil -->
                <p class="mc-sidebar-liveview__summary" data-testid="filter-summary">
                  Menampilkan
                  <strong>{{ filteredItems.length }}</strong>
                  dari <strong>{{ technicians.length }}</strong> teknisi
                  <span v-if="!activeButtons.all" class="text-muted">
                    ({{ activeStatusLabel }})
                  </span>
                </p>
              </header>

              <!-- ===== List Teknisi ===== -->
              <div class="mc-sidebar-liveview__list-wrapper">
                <!-- Loading -->
                <div v-if="isLoading" class="mc-state mc-state--loading" data-testid="sidebar-loading">
                  <b-spinner small variant="primary" />
                  <span class="ms-2">Memuat data teknisi...</span>
                </div>

                <!-- Empty -->
                <div v-else-if="!filteredItems.length" class="mc-state mc-state--empty" data-testid="sidebar-empty">
                  <i class="mc-fa icon-outline_search text-muted" aria-hidden="true"></i>
                  <h6 class="mt-2 mb-1">Tidak ada teknisi</h6>
                  <small class="text-muted">
                    Coba ubah filter atau kata kunci pencarian.
                  </small>
                </div>

                <!-- List -->
                <ul v-else class="mc-tech-list" data-testid="technician-list">
                  <li v-for="row in filteredItems" :key="row.user_id" class="mc-tech-item"
                    :class="`is-status-${(row.status || 'NIY').toLowerCase()}`"
                    :data-testid="`technician-item-${row.user_id}`" tabindex="0" @click="centering(row)"
                    @keydown.enter.space.prevent="centering(row)">
                    <div class="mc-tech-item__avatar" :class="avatarClass(row.status)">
                      {{ getInitials(row.name) }}
                    </div>

                    <div class="mc-tech-item__body">
                      <div class="mc-tech-item__header">
                        <h6 class="mc-tech-item__name" :title="row.name">{{ row.name }}</h6>
                        <span class="mc-tech-item__badge" :class="badgeClass(row.status)"
                          :title="`Status: ${statusLabel(row.status)}`">
                          <span class="status-dot" :class="dotClass(row.status)"></span>
                          {{ statusLabel(row.status) }}
                        </span>
                      </div>

                      <div class="mc-tech-item__meta">
                        <div class="mc-tech-item__meta-row" :title="loggedAtFull(row.logged_at)">
                          <i class="mc-fa icon-outline_eye" aria-hidden="true"></i>
                          <span>{{ row.logged_at ? momentDMHms(row.logged_at) : 'Belum ada aktivitas' }}</span>
                        </div>
                        <div class="mc-tech-item__meta-row" :title="row.location || ''">
                          <i class="mc-fa icon-outline_pin" aria-hidden="true"></i>
                          <span class="text-truncate-2">{{ row.location || 'Lokasi tidak tersedia' }}</span>
                        </div>
                      </div>
                      <div class="mc-tech-item__wo">
                        <div v-if="row.wo_number" class="mc-tech-item__meta-row mc-tech-item__meta-row--wo">
                          <i class="mc-fa fas fa-clipboard-list" aria-hidden="true"></i>

                          <span>
                            <strong>{{ row.wo_number }}</strong>
                          </span>

                          <span v-if="row.customer" class="text-muted">
                            — {{ row.customer }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </div>
  </aside>
</template>

<script>
import responsive from '@/mixin/responsive'
import { Helper } from '@/mixin/helper'
import { mapActions } from 'vuex'

const STATUS = Object.freeze({
  BUSY: 'B',
  AVAILABLE: 'A',
})

export default {
  name: 'SideBar',
  mixins: [Helper, responsive],

  data() {
    return {
      isLoading: false,
      search: { name: '' },
      activeButtons: {
        all: true,
        busy: false,
        available: false,
        notInstalledYet: false,
      },
    }
  },

  computed: {
    technicians() {
      return this.$store.state.users.technicians || []
    },
    sideBarOpen() {
      return this.$store.state.sidebar.sideBarOpen
    },

    counts() {
      const c = { all: this.technicians.length, busy: 0, available: 0, niy: 0 }
      this.technicians.forEach((t) => {
        if (t.status === STATUS.BUSY) c.busy++
        else if (t.status === STATUS.AVAILABLE) c.available++
        else if (t.status !== STATUS.BUSY && t.status !== STATUS.AVAILABLE) c.niy++
      })
      return c
    },

    filteredItems() {
      let list = this.technicians

      // Filter status
      if (!this.activeButtons.all) {
        list = list.filter((t) => {
          if (this.activeButtons.busy && t.status === STATUS.BUSY) return true
          if (this.activeButtons.available && t.status === STATUS.AVAILABLE) return true
          if (this.activeButtons.notInstalledYet && t.status !== STATUS.AVAILABLE && t.status !== STATUS.BUSY) return true
          return false
        })
      }

      // Filter search (case-insensitive)
      const q = (this.search.name || '').trim().toLowerCase()
      if (q) {
        list = list.filter((t) => (t.name || '').toLowerCase().includes(q))
      }
      return list
    },

    activeStatusLabel() {
      return [
        this.activeButtons.available && this.$t('location.sideBar.status.available'),
        this.activeButtons.busy && this.$t('location.sideBar.status.busy'),
        this.activeButtons.notInstalledYet && this.$t('location.sideBar.status.notInstalledYet'),
      ]
        .filter(Boolean)
        .join(', ')
    },
  },

  watch: {
    // Side-effect commit dipindah ke watch agar computed tetap pure
    filteredItems: {
      handler(val) {
        this.$store.commit('users/SET_USERS', val)
      },
      immediate: true,
    },
  },

  mounted() {
    this.loadData()
  },

  methods: {
    ...mapActions('users', ['loadTechnicians']),
    async loadData() {
      this.isLoading = true;
      try {
        await this.loadTechnicians();
      } finally {
        this.isLoading = false;
      }
    },

    centering(tech) {
      if (!tech) return;
      this.$store.commit('map/SET_SELECTED_TECHNICIAN', tech);
    },

    changeActiveButton(button) {
      if (button === 'all') {
        Object.keys(this.activeButtons).forEach((key) => {
          this.activeButtons[key] = key === 'all'
        })
        return
      }
      this.activeButtons[button] = !this.activeButtons[button]

      const allStatusesActive = ['busy', 'available', 'notInstalledYet'].every(
        (s) => this.activeButtons[s]
      )
      const noneActive = ['busy', 'available', 'notInstalledYet'].every(
        (s) => !this.activeButtons[s]
      )

      if (allStatusesActive || noneActive) {
        Object.keys(this.activeButtons).forEach((key) => {
          this.activeButtons[key] = key === 'all'
        })
      } else {
        this.activeButtons.all = false
      }
    },

    /* ===== Presentational helpers ===== */
    getInitials(name = '') {
      return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0])
        .join('')
        .toUpperCase() || '?'
    },

    statusLabel(status) {
      if (status === STATUS.BUSY) return this.$t('location.sideBar.status.busy')
      if (status === STATUS.AVAILABLE) return this.$t('location.sideBar.status.available')
      return this.$t('location.sideBar.status.notInstalledYet')
    },

    badgeClass(status) {
      if (status === STATUS.BUSY) return 'is-busy'
      if (status === STATUS.AVAILABLE) return 'is-available'
      return 'is-niy'
    },

    dotClass(status) {
      if (status === STATUS.BUSY) return 'status-dot--busy'
      if (status === STATUS.AVAILABLE) return 'status-dot--available'
      return 'status-dot--niy'
    },

    avatarClass(status) {
      if (status === STATUS.BUSY) return 'is-busy'
      if (status === STATUS.AVAILABLE) return 'is-available'
      return 'is-niy'
    },

    loggedAtFull(val) {
      return val ? this.momentDMHms(val) : 'Belum ada aktivitas'
    },
  },
}
</script>

<style scoped>
/* ===================================
   Sidebar Container
=================================== */
.mc-sidebar-liveview {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  overflow: hidden;
  /* penting */
}

.mc-sidebar-liveview__wrapper {
  flex: 1 1 0%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Tabs */
::v-deep .mc-sidebar-liveview__nav {
  padding: 0 16px;
  background: #ffffff;
  border-bottom: 1px solid #e5e9f0;
}

::v-deep .mc-sidebar-liveview__nav .nav-link {
  font-weight: 600;
  font-size: 0.875rem;
  color: #64748b;
  border: none;
  padding: 12px 16px;
}

::v-deep .mc-sidebar-liveview__nav .nav-link.active {
  color: #2563eb;
  background: transparent;
  border-bottom: 2px solid #2563eb;
}

::v-deep .mc-sidebar-liveview__content {
  flex: 1 1 0%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

::v-deep .mc-sidebar-liveview__content>.tab-pane {
  flex: 1 1 0%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

::v-deep .tab-pane {
  flex: 1 1 0%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mc-sidebar-liveview__fleet {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;

  display: flex;
  flex-direction: column;

  overflow: hidden;
}

.mc-sidebar-liveview__fleet-inner {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;

  display: flex;
  flex-direction: column;

  overflow: hidden;
}

/* ===================================
   Sticky Header: Filter + Search
=================================== */
.mc-sidebar-liveview__sticky-header {
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 20;
  background: #ffffff;
  padding: 16px 16px 12px;
  border-bottom: 1px solid #eef2f7;
}

/* Filter chips */
.mc-filter-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mc-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f1f5f9;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mc-filter-chip:hover {
  background: #e2e8f0;
}

.mc-filter-chip__count {
  background: #cbd5e1;
  color: #1e293b;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1.4;
}

.mc-filter-chip.is-active {
  color: #ffffff;
}

.mc-filter-chip.is-active .mc-filter-chip__count {
  background: rgba(255, 255, 255, 0.25);
  color: #ffffff;
}

.mc-filter-chip.is-active.is-all {
  background: #2563eb;
}

.mc-filter-chip.is-active.is-busy {
  background: #dc2626;
}

.mc-filter-chip.is-active.is-available {
  background: #16a34a;
}

.mc-filter-chip.is-active.is-niy {
  background: #d97706;
}

/* Search input */
.mc-sidebar-liveview__search ::v-deep .input-group-text {
  background: #f8fafc;
  border-color: #e5e9f0;
  color: #64748b;
}

.mc-sidebar-liveview__search ::v-deep .form-control {
  border-color: #e5e9f0;
  font-size: 0.875rem;
}

.mc-sidebar-liveview__search ::v-deep .form-control:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

/* Summary */
.mc-sidebar-liveview__summary {
  margin: 12px 0 0;
  font-size: 0.8125rem;
  color: #475569;
}

/* ===================================
   List Teknisi
=================================== */
.mc-sidebar-liveview__list-wrapper {
  flex: 1 1 auto;
  min-height: 0;

  overflow-y: auto;
  overflow-x: hidden;

  padding: 12px;
  background: #f8fafc;

  /* smooth scroll */
  -webkit-overflow-scrolling: touch;

  /* optional */
  overscroll-behavior: contain;
  max-height: calc(100vh - 360px);
  /* Total tinggi layar dikurangi tinggi header/filter sidebar Anda */
}

::v-deep .tab-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.mc-tech-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mc-tech-item {
  display: flex;
  gap: 12px;

  padding: 14px;
  background: #ffffff;

  border: 1px solid #eef2f7;
  border-left: 4px solid transparent;
  border-radius: 12px;

  cursor: pointer;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
  height: auto;
  min-height: unset;
}

.mc-tech-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
  transform: translateY(-1px);
}

.mc-tech-item:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.mc-tech-item.is-status-b {
  border-left-color: #dc2626;
}

.mc-tech-item.is-status-a {
  border-left-color: #16a34a;
}

.mc-tech-item.is-status-niy {
  border-left-color: #d97706;
}

.mc-tech-item__wo {
  margin-top: 4px;
  min-height: auto;
  display: flex;
  align-items: flex-start;
}

/* Avatar */
.mc-tech-item__avatar {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  color: #ffffff;
  background: #94a3b8;
}

.mc-tech-item__avatar.is-busy {
  background: #dc2626;
}

.mc-tech-item__avatar.is-available {
  background: #16a34a;
}

.mc-tech-item__avatar.is-niy {
  background: #d97706;
}

/* Body */
.mc-tech-item__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mc-tech-item__meta-row,
.mc-tech-item__meta-row--wo {
  word-break: break-word;
  overflow-wrap: anywhere;
}

.mc-tech-item__header {
  display: grid;
  grid-template-columns: 1fr auto;
  /* nama ambil sisa ruang, status tetap di kanan */
  align-items: baseline;
  gap: 8px;
}

.mc-tech-item__name {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #0f172a;
  /* tidak perlu white-space: nowrap -> biar wrap */
  word-break: break-word;
  overflow-wrap: anywhere;
  flex: 1 1 auto;
  min-width: 0;
}

.mc-tech-item__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

.mc-tech-item__badge.is-busy {
  background: #fee2e2;
  color: #b91c1c;
}

.mc-tech-item__badge.is-available {
  background: #dcfce7;
  color: #15803d;
}

.mc-tech-item__badge.is-niy {
  background: #fef3c7;
  color: #b45309;
}

/* Meta */
.mc-tech-item__meta {
  display: flex;
  flex-direction: column;
  gap: 6px;

  font-size: 0.8125rem;
  color: #64748b;

  overflow: hidden;
}

.mc-tech-item__meta-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  line-height: 1.45;
}

.mc-tech-item__meta-row span {
  flex: 1;
  min-width: 0;
}

.mc-tech-item__meta-row i {
  width: 14px;
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 0.8125rem;
}

.mc-tech-item__meta-row--wo {
  color: #334155;
  background: #f8fafc;
  padding: 6px 10px;
  border-radius: 6px;
  width: 100%;
  flex-wrap: wrap;
  word-break: break-word;
  gap: 4px;
}

.mc-tech-item__meta-row--wo strong {
  font-weight: 600;
  word-break: break-word;
}

.text-truncate-2 {
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: anywhere;
  display: inline-block;
  max-width: 100%;
}

/* ===================================
   Status dot helper
=================================== */
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #94a3b8;
}

.status-dot--busy {
  background: #dc2626;
}

.status-dot--available {
  background: #16a34a;
}

.status-dot--niy {
  background: #d97706;
}

/* ===================================
   Empty / Loading state
=================================== */
.mc-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  text-align: center;
  color: #64748b;
}

.mc-state--loading {
  flex-direction: row;
  padding: 24px 16px;
}

.mc-state--empty i {
  font-size: 2rem;
  opacity: 0.4;
}
</style>
